import { Component, input, output, signal, inject, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { TableModule, TableRowSelectEvent } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { UnidadAdministrativaRepository } from 'src/app/domain/service/unidad-administrativa/unidad-administrativa-repository';
import {
  sanitizeText,
  sanitizeTextPreserveSpaces
} from 'src/app/common/input-utils';
import {
  UnidadAdministrativa,
  UnidadAdministrativaFiltro,
  getUnidadAdministrativaNombreDisplay
} from '@model/unidad-administrativa/unidad-administrativa';
import { buscadorUnidadAdministrativaPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BaseBusquedaPaginadaDialog } from 'src/app/presentation/shared/busquedas/base-busqueda-paginada-dialog';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';

/**
 * Resultado de unidad administrativa seleccionada
 */
export interface UnidadAdministrativaSeleccionada {
  nmconsecutivo: number;
  cdunidadAdministrativa: string;
  dsunidadAdministrativa: string;
}

/**
 * Diálogo de búsqueda de unidades administrativas.
 *
 * Filtros:
 * - Código de unidad (búsqueda parcial)
 * - Nombre/descripción (búsqueda parcial)
 *
 * Solo muestra registros activos: FEBAJA IS NULL AND SNACTIVO = 'S'
 *
 * @author Sura - Migración GWT a Spring Boot
 */
@Component({
  selector: 'app-busqueda-unidad-administrativa-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    DropdownModule,
    ProgressSpinnerModule,
    TooltipModule,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      header="Filtros Consulta de Unidades Administrativas"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '48rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
      (onHide)="onCancelar()"
    >
      <!-- Sección Filtros de Búsqueda -->
      <div
        class="border dark:border-gray-600 rounded p-4 mb-4 bg-gray-50 dark:bg-gray-800"
      >
        <div class="font-medium text-gray-700 dark:text-gray-300 mb-3 text-sm">
          Filtros de Búsqueda
        </div>

        <!-- Código y Nombre (misma fila) -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-400 mb-1"
              >Código</label
            >
            <input
              pInputText
              [(ngModel)]="filtro.codigo"
              (input)="sanitizarCampo('codigo')"
              class="w-full"
              maxlength="20"
              placeholder="Código de unidad"
            />
          </div>
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-400 mb-1"
              >Nombre</label
            >
            <input
              pInputText
              [(ngModel)]="filtro.nombre"
              (input)="sanitizarCampo('nombre')"
              class="w-full"
              maxlength="100"
              placeholder="Nombre de la unidad"
            />
          </div>
        </div>

        <div
          class="flex flex-wrap gap-2 sm:gap-3 mt-4 justify-end sm:justify-start w-full"
        >
          <p-button
            icon="pi pi-eraser"
            label="Limpiar"
            severity="secondary"
            [outlined]="true"
            size="small"
            styleClass="min-h-[44px]"
            (onClick)="limpiarFiltros()"
          />
          <p-button
            icon="pi pi-search"
            label="Buscar"
            size="small"
            styleClass="min-h-[44px]"
            (onClick)="buscar()"
            [disabled]="loading()"
          />
        </div>
      </div>

      @if (mensaje()) {
        <div
          class="p-3 mb-3 rounded"
          [ngClass]="{
            'bg-yellow-100 text-yellow-800': mensajeTipo() === 'warning',
            'bg-blue-100 text-blue-800': mensajeTipo() === 'info',
            'bg-red-100 text-red-800': mensajeTipo() === 'error'
          }"
        >
          <i
            class="pi mr-2"
            [ngClass]="{
              'pi-exclamation-triangle': mensajeTipo() === 'warning',
              'pi-info-circle': mensajeTipo() === 'info',
              'pi-times-circle': mensajeTipo() === 'error'
            }"
          ></i>
          {{ mensaje() }}
        </div>
      }

      <!-- Loading -->
      @if (loading()) {
        <div class="flex justify-center p-4">
          <p-progressSpinner
            strokeWidth="4"
            [style]="{ width: '50px', height: '50px' }"
          ></p-progressSpinner>
        </div>
      }

      <!-- Listado de Unidades Administrativas -->
      <div class="border dark:border-gray-600 rounded">
        <div
          class="font-medium text-gray-700 dark:text-gray-300 p-3 border-b dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-sm flex justify-between items-center"
        >
          <span>Listado de Unidades Administrativas</span>
          @if (items().length > 0) {
            <span class="text-xs text-gray-500">
              Registros del {{ registroInicio() }} hasta el
              {{ registroFin() }} de {{ totalRegistros() }}
            </span>
          }
        </div>

        @if (!loading() && items().length > 0) {
          <div class="md:hidden space-y-3 p-3">
            @for (item of items(); track trackUnidad(item, $index)) {
              <div
                class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
              >
                <div class="mb-3 min-w-0">
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    Nombre
                  </div>
                  <div
                    class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                    [attr.title]="tituloTabla(getNombreDisplay(item))"
                  >
                    {{ textoTabla(getNombreDisplay(item)) }}
                  </div>
                </div>
                <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Código:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(item.cdunidadAdministrativa)
                    }}</span>
                  </div>
                </div>
                <p-button
                  label="Seleccionar"
                  icon="pi pi-check"
                  size="small"
                  styleClass="w-full min-h-[44px]"
                  (onClick)="seleccionarItem(item)"
                />
              </div>
            }
          </div>

          <div class="hidden md:block">
            <div
              role="region"
              [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
              class="sr-only"
            ></div>
            <p-table
              [resizableColumns]="true"
              columnResizeMode="expand"
              [value]="items()"
              [scrollable]="true"
              [responsiveLayout]="'scroll'"
              [scrollHeight]="tableConfig().responsive.scrollHeight || '250px'"
              [selectionMode]="tableSelectionMode()"
              [(selection)]="seleccionado"
              (onRowSelect)="onRowSelect($event)"
              [tableStyle]="{
                'min-width': tableConfig().responsive.minWidth || 'max-content'
              }"
              [dataKey]="
                (tableConfig().selection.dataKey || 'nmconsecutivo').toString()
              "
              [styleClass]="tableStyleClass()"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Código"
                      >Código</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Nombre">Nombre</span>
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Seleccionar"
                      >Seleccionar</span
                    >
                  </th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-item>
                <tr [pSelectableRow]="item">
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="tituloTabla(item.cdunidadAdministrativa)"
                      >{{ textoTabla(item.cdunidadAdministrativa) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="tituloTabla(getNombreDisplay(item))"
                      >{{ textoTabla(getNombreDisplay(item)) }}</span
                    >
                  </td>
                  <td>
                    <button
                      pButton
                      label="Seleccionar"
                      class="p-button-sm p-button-text"
                      (click)="seleccionarItem(item)"
                    ></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </div>

          <app-busqueda-paginador
            [currentPage]="paginaActual()"
            [totalPages]="totalPaginas()"
            [hasPrevious]="hasPrevious()"
            [hasNext]="hasNext()"
            [pageSizeOptions]="pageSizeOptions"
            [selectedPageSize]="selectedPageSize"
            (pageSizeChange)="onSelectedPageSizeChange($event)"
            (first)="irPrimeraPagina()"
            (previous)="loadPreviousPage()"
            (next)="loadNextPage()"
            (last)="irUltimaPagina()"
          ></app-busqueda-paginador>
        }

        @if (!loading() && items().length === 0 && hasBuscado()) {
          <div class="text-center text-gray-500 p-4">
            No se encontraron unidades administrativas con los criterios
            especificados
          </div>
        }

        @if (!loading() && items().length === 0 && !hasBuscado()) {
          <div class="text-center text-gray-500 p-4">No hay datos</div>
        }
      </div>

      <!-- Botón footer -->
      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Cerrar"
            icon="pi pi-times"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="onCancelar()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class BusquedaUnidadAdministrativaDialogComponent extends BaseBusquedaPaginadaDialog<UnidadAdministrativa> {
  // Inputs/Outputs
  visible = true;
  seleccionado: UnidadAdministrativa | null = null;

  /** Output: unidad administrativa seleccionada */
  unidadAdministrativaSelected = output<UnidadAdministrativaSeleccionada>();

  /** Output: diálogo cerrado sin selección */
  dialogClosed = output<void>();

  /** Legacy: filtra solo unidades vinculadas al cliente/aseguradora activo */
  nmconsecutivoCliente = input<number | null | undefined>(undefined);

  // Servicios
  private readonly repository = inject(UnidadAdministrativaRepository);

  hasBuscado = signal(false);

  // paginaActual (1-based, de la base) solo alimenta las etiquetas. Los
  // botones Anterior/Siguiente dependen de la respuesta del servidor (skill
  // frontend-girop §17.7), por eso se sobreescriben los derivados de la base.
  override hasPrevious = signal(false);
  override hasNext = signal(false);

  // Filtros
  filtro: UnidadAdministrativaFiltro = {};

  readonly tableConfig = computed(() =>
    buscadorUnidadAdministrativaPreset.createConfig({
      data: this.items() as unknown as Record<string, unknown>[],
      selection: {
        mode: 'single',
        dataKey: 'nmconsecutivo',
        selected: this.seleccionado as unknown as Record<string, unknown> | null
      },
      style: {
        tableClassName: 'tabla-buscador-unidad-administrativa',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.loading(),
        emptyMessage:
          'No se encontraron unidades administrativas con los criterios especificados'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: 'max-content',
        scrollHeight: '250px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de busqueda de unidades administrativas',
        announceSelection: true,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = this.buildTableStyleClass(this.tableConfig);

  readonly tableSelectionMode = this.buildTableSelectionMode(this.tableConfig);

  /**
   * Abre el diálogo de búsqueda
   */
  open(): void {
    this.visible = true;
    this.limpiarFiltros();
  }

  /**
   * Cierra el diálogo
   */
  close(): void {
    this.visible = false;
  }

  /**
   * Ejecuta la búsqueda de unidades administrativas
   */
  async buscar(): Promise<void> {
    this.sanitizarCampo('codigo', true);
    this.sanitizarCampo('nombre', true);
    this.hasBuscado.set(true);
    // Búsqueda nueva: siempre página 1
    await this.ejecutarBusqueda(0);
  }

  /**
   * Sanitización XSS de texto libre (seguridad-girop §3). Mientras se teclea
   * conserva los espacios; antes de buscar también los recorta.
   */
  sanitizarCampo(campo: 'codigo' | 'nombre', recortar = false): void {
    const valor = this.filtro[campo];
    if (valor === undefined) return;
    this.filtro[campo] = recortar
      ? sanitizeText(valor)
      : sanitizeTextPreserveSpaces(valor);
  }

  protected override shouldTriggerSearchOnPageSizeChange(): boolean {
    return this.hasBuscado();
  }

  /** Cursor devuelto por el backend para pedir la página siguiente */
  private nextCursorActual = signal<string | null>(null);

  /** Cursor devuelto por el backend para pedir la página anterior */
  private previousCursorActual = signal<string | null>(null);

  private async buscarPagina(
    cursor: string | null,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST',
    pagina = 1
  ): Promise<void> {
    this.loading.set(true);
    this.mensaje.set('');

    try {
      const response = await this.repository.buscarUnidadesAdministrativas(
        {
          ...this.filtro,
          nmconsecutivoCliente: this.nmconsecutivoCliente() ?? undefined
        },
        cursor,
        this.pageSize(),
        direction
      );

      const content = Array.isArray(response?.content) ? response.content : [];
      const totalElements =
        typeof response?.totalElements === 'number'
          ? response.totalElements
          : content.length;

      this.items.set(content);
      this.totalRegistros.set(totalElements);
      this.nextCursorActual.set(response?.nextCursor ?? null);
      this.previousCursorActual.set(response?.previousCursor ?? null);

      this.hasNext.set(response?.hasNext ?? false);
      this.hasPrevious.set(response?.hasPrevious ?? false);

      // La página (1-based) solo cambia tras una respuesta exitosa. Con LAST
      // se calcula a partir del total que devuelve el servidor.
      if (direction === 'LAST') {
        const size = this.pageSize();
        this.paginaActual.set(
          size > 0 && totalElements > 0 ? Math.max(1, Math.ceil(totalElements / size)) : 1
        );
      } else {
        this.paginaActual.set(pagina);
      }

      if (content.length === 0) {
        this.mensaje.set(
          'No se encontraron unidades administrativas con los criterios especificados'
        );
        this.mensajeTipo.set('warning');
      }
    } catch (error) {
      console.error('Error buscando unidades administrativas:', error);
      this.mensaje.set(
        this.resolverMensajeErrorBusqueda(
          error,
          'Error al buscar unidades administrativas'
        )
      );
      this.mensajeTipo.set('error');
      this.items.set([]);
      this.totalRegistros.set(0);
    } finally {
      this.loading.set(false);
    }
  }

  protected override async ejecutarBusqueda(_start: number): Promise<void> {
    await this.buscarPagina(null, 'NEXT', 1);
  }

  /**
   * Limpia los filtros y resultados
   */
  limpiarFiltros(): void {
    this.filtro = {};
    this.items.set([]);
    this.clearMensajes();
    this.resetPaginacion();
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.hasBuscado.set(false);
    this.seleccionado = null;
  }

  override async onPageSizeChange(): Promise<void> {
    this.pageSize.set(this.selectedPageSize);
    this.paginaActual.set(1);
    if (!this.shouldTriggerSearchOnPageSizeChange()) {
      return;
    }
    await this.ejecutarBusqueda(0);
  }

  override async irPrimeraPagina(): Promise<void> {
    if (this.loading()) {
      return;
    }
    await this.buscarPagina(null, 'NEXT', 1);
  }

  override async loadPreviousPage(): Promise<void> {
    if (this.loading() || !this.hasPrevious()) {
      return;
    }
    await this.buscarPagina(
      this.previousCursorActual(),
      'PREVIOUS',
      Math.max(1, this.paginaActual() - 1)
    );
  }

  override async loadNextPage(): Promise<void> {
    if (this.loading() || !this.hasNext()) {
      return;
    }
    await this.buscarPagina(
      this.nextCursorActual(),
      'NEXT',
      this.paginaActual() + 1
    );
  }

  override async irUltimaPagina(): Promise<void> {
    if (this.loading() || this.totalRegistros() === 0) {
      return;
    }
    await this.buscarPagina(null, 'LAST');
  }

  /**
   * Maneja la selección de una fila
   */
  onRowSelect(event: TableRowSelectEvent<UnidadAdministrativa>): void {
    if (event.data && !Array.isArray(event.data)) {
      this.seleccionado = event.data;
    }
  }

  /**
   * Obtiene el nombre para mostrar de una unidad administrativa
   */
  getNombreDisplay(unidad: UnidadAdministrativa): string {
    return getUnidadAdministrativaNombreDisplay(unidad);
  }

  trackUnidad(item: UnidadAdministrativa, index: number): string | number {
    const consecutivo = item?.nmconsecutivo;
    if (
      consecutivo !== null &&
      consecutivo !== undefined &&
      String(consecutivo).trim() !== ''
    ) {
      return consecutivo;
    }

    const codigo = String(item?.cdunidadAdministrativa ?? '').trim();
    if (codigo) {
      return `${codigo}-${index}`;
    }

    return index;
  }

  /**
   * Selecciona una unidad administrativa directamente desde la tabla
   */
  seleccionarItem(item: UnidadAdministrativa): void {
    const resultado: UnidadAdministrativaSeleccionada = {
      nmconsecutivo: item.nmconsecutivo,
      cdunidadAdministrativa: item.cdunidadAdministrativa,
      dsunidadAdministrativa: item.dsunidadAdministrativa
    };
    this.unidadAdministrativaSelected.emit(resultado);
    this.close();
  }

  /**
   * Emite la unidad administrativa seleccionada y cierra el diálogo
   */
  onSeleccionar(): void {
    if (this.seleccionado) {
      this.seleccionarItem(this.seleccionado);
    }
  }

  /**
   * Cierra el diálogo sin selección
   */
  onCancelar(): void {
    this.dialogClosed.emit();
    this.close();
  }

}
